// HaukeApi - Spezielle Admin-API für die Verwaltung großer Datenmengen
// Für 50.000+ Direkterzeuger-Einträge mit server-side Pagination

const HAUKE_API_BASE_URL = 'https://europe-west3-lf1-app.cloudfunctions.net/haukeAdmin';
const HAUKE_TASKS_API_BASE_URL = 'https://europe-west3-lf1-app.cloudfunctions.net/haukeTasksAdminOptimized';

class HaukeApiService {
  constructor() {
    this.baseUrl = HAUKE_API_BASE_URL;
    this.tasksBaseUrl = HAUKE_TASKS_API_BASE_URL;
  }
  // ===== USER SUGGESTIONS =====

  async getUserSuggestions(params = {}) {
    const url = new URL('https://europe-west3-lf1-app.cloudfunctions.net/userSuggestions');
    url.searchParams.set('status', params.status || 'PENDING');
    if (params.search) url.searchParams.set('search', params.search);
    if (params.dateFrom) url.searchParams.set('dateFrom', params.dateFrom);
    if (params.dateTo) url.searchParams.set('dateTo', params.dateTo);
    if (params.limit) url.searchParams.set('limit', params.limit);
    if (params.page) url.searchParams.set('page', params.page);

    const response = await fetch(url.toString(), {
      headers: { 'Content-Type': 'application/json' }
    });
    const data = await response.json();
    if (!response.ok || !data.success) {
      throw new Error(data.error || `HTTP error! status: ${response.status}`);
    }
    return data;
  }

  async getUserSuggestionsStats() {
    const url = 'https://europe-west3-lf1-app.cloudfunctions.net/userSuggestions/stats';
    const response = await fetch(url);
    const data = await response.json();
    if (!response.ok || !data.success) {
      throw new Error(data.error || `HTTP error! status: ${response.status}`);
    }
    return data;
  }

  // ===== USER DECLINED (Geschlossene Meldungen) =====

  async getUserDeclined(params = {}) {
    const url = new URL('https://europe-west3-lf1-app.cloudfunctions.net/userDeclined');
    if (params.search) url.searchParams.set('search', params.search);
    if (params.dateFrom) url.searchParams.set('dateFrom', params.dateFrom);
    if (params.dateTo) url.searchParams.set('dateTo', params.dateTo);
    if (params.limit) url.searchParams.set('limit', params.limit);
    if (params.page) url.searchParams.set('page', params.page);

    const response = await fetch(url.toString(), {
      headers: { 'Content-Type': 'application/json' }
    });
    const data = await response.json();
    if (!response.ok || !data.success) {
      throw new Error(data.error || `HTTP error! status: ${response.status}`);
    }
    return data;
  }

  async getUserDeclinedStats() {
    const url = 'https://europe-west3-lf1-app.cloudfunctions.net/userDeclined/stats';
    const response = await fetch(url);
    const data = await response.json();
    if (!response.ok || !data.success) {
      throw new Error(data.error || `HTTP error! status: ${response.status}`);
    }
    return data;
  }

  async createUserDeclined(payload) {
    const url = 'https://europe-west3-lf1-app.cloudfunctions.net/userDeclined';
    const response = await fetch(url, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(payload)
    });
    const data = await response.json();
    if (!response.ok || !data.success) {
      throw new Error(data.error || `HTTP error! status: ${response.status}`);
    }
    return data;
  }

  async updateUserDeclined(id, update) {
    const url = `https://europe-west3-lf1-app.cloudfunctions.net/userDeclined/${encodeURIComponent(id)}`;
    const response = await fetch(url, {
      method: 'PUT',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(update)
    });
    const data = await response.json();
    if (!response.ok || !data.success) {
      throw new Error(data.error || `HTTP error! status: ${response.status}`);
    }
    return data;
  }

  async deleteUserDeclined(id) {
    const url = `https://europe-west3-lf1-app.cloudfunctions.net/userDeclined/${encodeURIComponent(id)}`;
    const response = await fetch(url, { method: 'DELETE' });
    const data = await response.json();
    if (!response.ok || !data.success) {
      throw new Error(data.error || `HTTP error! status: ${response.status}`);
    }
    return data;
  }


  // Helper für API Requests
  async request(endpoint, options = {}) {
    const url = `${this.baseUrl}${endpoint}`;
    const config = {
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${this.getAuthToken()}`, // Admin-Token
        ...options.headers
      },
      ...options
    };

    try {
      const response = await fetch(url, config);
      
      if (!response.ok) {
        const errorData = await response.json().catch(() => ({ message: 'Unknown error' }));
        throw new Error(errorData.message || `HTTP error! status: ${response.status}`);
      }
      
      const data = await response.json();
      return data;
    } catch (error) {
      console.error(`HaukeApi Request failed: ${endpoint}`, error);
      throw error;
    }
  }

  // Helper für Tasks API Requests
  async requestTasks(endpoint, options = {}) {
    const url = `${this.tasksBaseUrl}${endpoint}`;
    const config = {
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${this.getAuthToken()}`, // Admin-Token
        ...options.headers
      },
      ...options
    };

    try {
      const response = await fetch(url, config);
      
      if (!response.ok) {
        const errorData = await response.json().catch(() => ({ message: 'Unknown error' }));
        throw new Error(errorData.message || `HTTP error! status: ${response.status}`);
      }
      
      const data = await response.json();
      return data;
    } catch (error) {
      console.error(`HaukeTasksApi Request failed: ${endpoint}`, error);
      throw error;
    }
  }

  // Auth Token für Admin-Zugriff
  getAuthToken() {
    // Firebase Auth Token aus dem AuthContext holen
    const user = JSON.parse(localStorage.getItem('user') || '{}');
    return 'hauke-admin-token'; // Temporärer Fix für Development
  }

  // ===== DIREKTERZEUGER MANAGEMENT =====

  /**
   * Lädt Direkterzeuger mit echter server-seitiger Verarbeitung
   * @param {Object} params - Query-Parameter
   * @param {number} params.page - Seitenzahl (1-basiert)
   * @param {number} params.limit - Anzahl pro Seite (default: 100)
   * @param {string} params.search - Allgemeine Suche (server-seitige Prefix-Suche)
   * @param {string} params.name - Spezifische Name-Suche
   * @param {string} params.plz - Spezifische PLZ-Suche
   * @param {string} params.city - Spezifische Stadt-Suche
   * @param {string} params.membership - Spezifische Mitgliedschaft-Suche
   * @param {string} params.sortBy - Sortierfeld (name, city, postal_code, category)
   * @param {string} params.sortOrder - Sortierrichtung (asc, desc)
   * @param {string} params.lastDocId - Cursor für echte Pagination (optional)
   */
  async getDirekterzeuger(params = {}) {
    const queryParams = new URLSearchParams({
      page: params.page || 1,
      limit: params.limit || 100,
      search: params.search || '',
      name: params.name || '',
      plz: params.plz || '',
      city: params.city || '',
      membership: params.membership || '',
      sortBy: params.sortBy || 'name',
      sortOrder: params.sortOrder || 'asc',
      lastDocId: params.lastDocId || ''
    });

    return this.request(`/?${queryParams}`);
  }

  /**
   * Lädt vollständige Details eines Direkterzeugers inklusive aller Subcollections
   * @param {string} hofId - Die HofId des Direkterzeugers
   */
  async getDirekterzeugerDetails(hofId) {
    if (!hofId) {
      throw new Error('HofId is required');
    }
    
    return this.request(`/direkterzeuger/${hofId}`);
  }

  /**
   * Erstellt einen neuen Direkterzeuger-Eintrag
   * @param {Object} data - Direkterzeuger-Daten
   */
  async createDirekterzeuger(data) {
    return this.request('/direkterzeuger', {
      method: 'POST',
      body: JSON.stringify(data)
    });
  }

  /**
   * Aktualisiert einen Direkterzeuger-Eintrag
   * @param {string} id - Direkterzeuger-ID
   * @param {Object} data - Aktualisierte Daten
   */
  async updateDirekterzeuger(id, data) {
    return this.request(`/direkterzeuger/${id}`, {
      method: 'PUT',
      body: JSON.stringify(data)
    });
  }

  /**
   * Löscht einen Direkterzeuger-Eintrag
   * @param {string} id - Direkterzeuger-ID
   */
  async deleteDirekterzeuger(id) {
    return this.request(`/direkterzeuger/${id}`, {
      method: 'DELETE'
    });
  }

  /**
   * Bulk-Update für mehrere Direkterzeuger
   * @param {Array} updates - Array von {id, data} Objekten
   */
  async bulkUpdateDirekterzeuger(updates) {
    return this.request('/direkterzeuger/bulk', {
      method: 'PUT',
      body: JSON.stringify({ updates })
    });
  }

  /**
   * Bulk-Delete für mehrere Direkterzeuger
   * @param {Array} ids - Array von Direkterzeuger-IDs
   */
  async bulkDeleteDirekterzeuger(ids) {
    return this.request('/direkterzeuger/bulk', {
      method: 'DELETE',
      body: JSON.stringify({ ids })
    });
  }

  // ===== STATISTIKEN UND ANALYTICS =====

  /**
   * Lädt Statistiken über Direkterzeuger
   */
  async getDirekterzeugerStats() {
    return this.request('/direkterzeuger/stats');
  }

  /**
   * Lädt Kategorien mit Anzahl der Direkterzeuger
   */
  async getCategories() {
    return this.request('/direkterzeuger/categories');
  }

  /**
   * Lädt Städte mit Anzahl der Direkterzeuger
   */
  async getCities() {
    return this.request('/direkterzeuger/cities');
  }

  // ===== EXPORT/IMPORT =====

  /**
   * Exportiert Direkterzeuger-Daten als CSV
   * @param {Object} filters - Filter-Parameter
   */
  async exportDirekterzeuger(filters = {}) {
    const queryParams = new URLSearchParams(filters);
    return this.request(`/direkterzeuger/export?${queryParams}`);
  }

  /**
   * Importiert Direkterzeuger-Daten aus CSV
   * @param {File} file - CSV-Datei
   */
  async importDirekterzeuger(file) {
    const formData = new FormData();
    formData.append('file', file);

    return this.request('/direkterzeuger/import', {
      method: 'POST',
      body: formData,
      headers: {} // Keine Content-Type für FormData
    });
  }

  // ===== VALIDIERUNG =====

  /**
   * Validiert Direkterzeuger-Daten
   * @param {Object} data - Zu validierende Daten
   */
  async validateDirekterzeuger(data) {
    return this.request('/direkterzeuger/validate', {
      method: 'POST',
      body: JSON.stringify(data)
    });
  }

  /**
   * Sucht nach Duplikaten
   * @param {Object} criteria - Suchkriterien
   */
  async findDuplicates(criteria) {
    return this.request('/direkterzeuger/duplicates', {
      method: 'POST',
      body: JSON.stringify(criteria)
    });
  }

  // ===== AUFGABEN & NOTIZEN MANAGEMENT =====

  /**
   * Lädt alle Aufgaben über alle Direkterzeuger hinweg
   * @param {Object} params - Filter-Parameter
   * @param {string} params.status - Filter nach Status: 'pending', 'completed', 'overdue'
   * @param {string} params.priority - Filter nach Priorität: 'low', 'normal', 'high', 'urgent'
   * @param {string} params.dateFrom - Filter ab Datum (ISO-Date)
   * @param {string} params.dateTo - Filter bis Datum (ISO-Date)
   * @param {string} params.hofId - Filter nach spezifischem Direkterzeuger
   * @param {number} params.limit - Anzahl der Ergebnisse (default: 100)
   * @param {number} params.page - Seite (default: 1)
   */
  async getAllTasks(params = {}) {
    const queryParams = new URLSearchParams({
      status: params.status || '',
      priority: params.priority || '',
      dateFrom: params.dateFrom || '',
      dateTo: params.dateTo || '',
      hofId: params.hofId || '',
      search: params.search || '',
      limit: params.limit || 100,
      page: params.page || 1
    });

    return this.requestTasks(`/tasks?${queryParams}`);
  }

  /**
   * Erstellt eine neue Aufgabe
   * @param {Object} taskData - Aufgaben-Daten
   */
  async createTask(taskData) {
    return this.requestTasks('/tasks', {
      method: 'POST',
      body: JSON.stringify(taskData)
    });
  }

  /**
   * Lädt alle Aufgaben für einen spezifischen Datumsbereich (für Kalender)
   * @param {string} dateFrom - Start-Datum (ISO-Date)
   * @param {string} dateTo - End-Datum (ISO-Date)
   */
  async getTasksForDateRange(dateFrom, dateTo) {
    const queryParams = new URLSearchParams({
      dateFrom,
      dateTo,
      onlyTasks: 'true' // Nur Aufgaben, keine einfachen Notizen
    });

    return this.requestTasks(`/tasks/calendar?${queryParams}`);
  }

  /**
   * Lädt überfällige Aufgaben
   * @param {number} limit - Anzahl der Ergebnisse (default: 50)
   */
  async getOverdueTasks(limit = 50) {
    return this.requestTasks(`/tasks/overdue?limit=${limit}`);
  }

  /**
   * Lädt heute fällige Aufgaben
   */
  async getTodaysTasks() {
    const today = new Date().toISOString().split('T')[0];
    return this.getTasksForDateRange(today, today);
  }

  /**
   * Lädt Aufgaben-Statistiken
   */
  async getTasksStats() {
    return this.requestTasks('/tasks/stats');
  }

  /**
   * Aktualisiert eine einzelne Aufgabe
   * @param {string} hofId - Direkterzeuger ID
   * @param {string} taskId - Aufgaben ID
   * @param {Object} updates - Aktualisierungen
   */
  async updateTask(hofId, taskId, updates) {
    return this.requestTasks(`/tasks/${taskId}`, {
      method: 'PUT',
      body: JSON.stringify({
        ...updates,
        hofId: hofId // hofId als Body-Parameter
      })
    });
  }

  /**
   * Markiert eine Aufgabe als erledigt/nicht erledigt
   * @param {string} hofId - Direkterzeuger ID
   * @param {string} taskId - Aufgaben ID
   * @param {boolean} completed - Erledigt-Status
   */
  async toggleTask(hofId, taskId, completed) {
    return this.updateTask(hofId, taskId, { 
      completed, 
      updatedAt: new Date().toISOString() 
    });
  }

  /**
   * Löscht eine Aufgabe
   * @param {string} hofId - Direkterzeuger ID
   * @param {string} taskId - Aufgaben ID
   */
  async deleteTask(hofId, taskId) {
    return this.requestTasks(`/tasks/${taskId}`, {
      method: 'DELETE',
      body: JSON.stringify({ hofId: hofId })
    });
  }

  // ===== PRODUCER FEEDBACK MANAGEMENT =====

  async listProducerFeedback(params = {}) {
    const qp = new URLSearchParams({
      farmId: params.farmId || '',
      userId: params.userId || '',
      category: params.category || '',
      status: params.status || '',
      q: params.q || '',
      from: params.from || '',
      to: params.to || '',
      limit: params.limit || 50,
      cursor: params.cursor || '',
      sort: params.sort || 'desc'
    });
    return this.request(`/producerFeedback?${qp.toString()}`);
  }

  async getProducerFeedback(id) {
    return this.request(`/producerFeedback/${encodeURIComponent(id)}`);
  }

  async createProducerFeedback(payload) {
    return this.request(`/producerFeedback`, {
      method: 'POST',
      body: JSON.stringify(payload)
    });
  }

  async updateProducerFeedback(id, update) {
    return this.request(`/producerFeedback/${encodeURIComponent(id)}`, {
      method: 'PUT',
      body: JSON.stringify(update)
    });
  }

  async deleteProducerFeedback(id) {
    return this.request(`/producerFeedback/${encodeURIComponent(id)}`, {
      method: 'DELETE'
    });
  }

  /**
   * Löscht einen Direkterzeuger/Hof komplett
   * @param {string} hofId - Direkterzeuger ID
   */
  async deleteHof(hofId) {
    return this.request(`/direkterzeuger/${hofId}`, {
      method: 'DELETE'
    });
  }

  /**
   * Löst den Versand der Community-Products-Mail für einen Hof aus
   * @param {string} hofId - Direkterzeuger ID
   */
  async sendCommunityMail(hofId) {
    if (!hofId) throw new Error('hofId is required');
    return this.request(`/community/mail`, {
      method: 'POST',
      body: JSON.stringify({ hofId })
    });
  }

  /**
   * Versendet eine allgemeine Welcome-Mail an den Hof
   * @param {string} hofId
   */
  async sendWelcomeMail(hofId) {
    if (!hofId) throw new Error('hofId is required');
    return this.request(`/community/welcome`, {
      method: 'POST',
      body: JSON.stringify({ hofId })
    });
  }
}

// Singleton Instance
const haukeApiService = new HaukeApiService();

export default haukeApiService;

// Export für TypeScript-Typen (falls später TypeScript verwendet wird)
export const API_RESPONSE_STRUCTURE = {
  // Beispiel-Antwort-Struktur
  data: [
    {
      id: 'string',
      name: 'string',
      producer_name: 'string',
      city: 'string',
      postal_code: 'string',
      street: 'string',
      phone: 'string',
      email: 'string',
      website: 'string',
      category: 'string',
      description: 'string',
      created_at: 'timestamp',
      updated_at: 'timestamp',
      hofId: 'string'
    }
  ],
  pagination: {
    page: 1,
    limit: 100,
    total: 50000,
    totalPages: 500,
    hasNext: true,
    hasPrev: false
  },
  filters: {
    search: 'string',
    category: 'string',
    city: 'string'
  }
};

// Konfiguration für die API
export const HAUKE_API_CONFIG = {
  maxItemsPerPage: 500,
  defaultItemsPerPage: 100,
  maxBulkOperations: 1000,
  supportedExportFormats: ['csv', 'xlsx', 'json'],
  supportedImportFormats: ['csv', 'xlsx']
}; 